import { useMemo } from 'react';
import { composeBlockOrder, BLOCK_META, getEstimatedPageCount } from '@ellie/shared';
import type { BlockSlot, TopicCode } from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';

export function CompositionPreview() {
  const { profile } = useStudioStore();

  const slots = useMemo<BlockSlot[]>(() => {
    if (profile.topics.length === 0) return [];
    return composeBlockOrder({
      pace: profile.pace,
      topics: profile.topics as TopicCode[],
      workbookTheme: 'Preview',
      learningFocus: profile.learning_focus ?? undefined,
    });
  }, [profile.pace, profile.topics, profile.learning_focus]);

  if (slots.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-text-light">
        Select at least one topic to see the composition.
      </p>
    );
  }

  const pageEstimate = getEstimatedPageCount(profile.pace);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-xs text-text-muted">
        <span>{slots.length} blocks</span>
        <span>~{pageEstimate} pages</span>
      </div>

      {profile.learning_focus && profile.learning_focus.length > 0 && (
        <p className="mb-2 text-[10px] text-amber-600">
          Focus adjustments active — block weights shifted toward{' '}
          {profile.learning_focus.join(', ')}
        </p>
      )}

      <div className="space-y-1">
        {slots.map((slot) => {
          const meta = BLOCK_META[slot.type];
          return (
            <div
              key={slot.order}
              className="flex items-center gap-2 rounded border border-border px-2.5 py-1.5"
            >
              <span className="text-xs font-medium text-text-light w-5 text-right">
                {slot.order}
              </span>
              <span
                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold text-white"
                style={{ backgroundColor: meta.color }}
              >
                {meta.name}
              </span>
              <span className="flex-1 truncate text-xs text-text-muted">{slot.subTheme}</span>
              <span className="text-[10px] text-text-light">~{meta.estimatedPages}p</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
